/** Set or clear the price override for one AI model. Rates are USD per 1M tokens. */

import { useState, useEffect, type ReactElement, type FormEvent } from 'react';
import type { AiModel, EffectivePrice, PriceOverrideBody } from '@services/aiModelsService';
import { extractErrorDetail } from '../utils/errorDetail';
import { useAiModelPrice, useSetAiModelPrice, useClearAiModelPrice } from '../hooks/useAiModels';

interface AiModelPriceDialogProps {
  model: AiModel;
  onClose: () => void;
}

interface PriceForm {
  input: string;
  output: string;
  cacheRead: string;
  cacheWrite: string;
}

const EMPTY: PriceForm = { input: '', output: '', cacheRead: '', cacheWrite: '' };

const FIELDS: { key: keyof PriceForm; label: string; required: boolean }[] = [
  { key: 'input', label: 'Input (USD per 1M tokens)', required: true },
  { key: 'output', label: 'Output (USD per 1M tokens)', required: true },
  { key: 'cacheRead', label: 'Cache read (USD per 1M tokens)', required: true },
  { key: 'cacheWrite', label: 'Cache write (USD per 1M tokens)', required: false },
];

function buildBody(form: PriceForm): PriceOverrideBody {
  const body: PriceOverrideBody = {
    input_per_mtok: Number(form.input),
    output_per_mtok: Number(form.output),
    cache_read_per_mtok: Number(form.cacheRead),
  };
  if (form.cacheWrite.trim() !== '') body.cache_write_per_mtok = Number(form.cacheWrite);
  return body;
}

/** Where today's price comes from, so an admin knows Save writes an override. */
function PriceSourceNote({ current }: { current: EffectivePrice | null | undefined }): ReactElement | null {
  if (current === undefined) return null; // still loading
  if (current === null) {
    return <p className="dialog__hint">No price is set for this model. Saving creates an override.</p>;
  }
  const effect = current.source === 'override' ? 'Saving replaces it.' : 'Saving creates an override.';
  return <p className="dialog__hint">Current price source: {current.source}. {effect}</p>;
}

export function AiModelPriceDialog({ model, onClose }: AiModelPriceDialogProps): ReactElement {
  const { data: current } = useAiModelPrice(model.model_id);
  const setPrice = useSetAiModelPrice();
  const clearPrice = useClearAiModelPrice();
  const [form, setForm] = useState<PriceForm>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!current) return;
    setForm({
      input: String(current.input_per_mtok),
      output: String(current.output_per_mtok),
      cacheRead: current.cache_read_per_mtok == null ? '' : String(current.cache_read_per_mtok),
      cacheWrite: current.cache_write_per_mtok == null ? '' : String(current.cache_write_per_mtok),
    });
  }, [current]);

  const fail = (err: unknown): void => { setError(extractErrorDetail(err)); };

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    setError(null);
    setPrice.mutate(
      { modelId: model.model_id, body: buildBody(form) },
      { onSuccess: onClose, onError: fail },
    );
  };

  const handleClear = (): void => {
    setError(null);
    clearPrice.mutate(model.model_id, { onSuccess: onClose, onError: fail });
  };

  return (
    <div className="dialog-overlay" onClick={onClose} role="presentation">
      <dialog
        className="dialog"
        open
        onClick={(e) => { e.stopPropagation(); }}
        aria-label={`Price for ${model.model_id}`}
      >
        <h2>Price: {model.model_id}</h2>
        <PriceSourceNote current={current} />
        <form onSubmit={handleSubmit} className="dialog__form">
          {FIELDS.map(({ key, label, required }) => (
            <label key={key} className="dialog__field">
              {label}
              <input
                type="number"
                min={0}
                step="any"
                value={form[key]}
                required={required}
                onChange={(e) => { setForm((prev) => ({ ...prev, [key]: e.target.value })); }}
              />
            </label>
          ))}
          {error && <div className="dialog__error" role="alert">{error}</div>}
          <div className="dialog__actions">
            <button type="button" onClick={onClose} className="dialog__btn dialog__btn--secondary">
              Cancel
            </button>
            <button
              type="button"
              onClick={handleClear}
              disabled={clearPrice.isPending}
              className="dialog__btn dialog__btn--secondary"
            >
              Clear override
            </button>
            <button type="submit" className="dialog__btn dialog__btn--primary" disabled={setPrice.isPending}>
              {setPrice.isPending ? 'Saving...' : 'Save override'}
            </button>
          </div>
        </form>
      </dialog>
    </div>
  );
}
